import type { Dict } from './ru';

const en: Dict = {
  app: { name: 'Vatman', tagline: 'Online whiteboard for teams' },
  common: {
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    rename: 'Rename',
    close: 'Close',
    copy: 'Copy',
    done: 'Done',
    loading: 'Loading…',
    back: 'Back',
    search: 'Search',
    create: 'Create',
    invite: 'Invite',
    remove: 'Remove',
    leave: 'Leave',
    you: 'you',
    untitled: 'Untitled',
    open: 'Open',
    settings: 'Settings',
    members: 'Members',
    more: 'More',
    retry: 'Retry',
    copied: 'Copied',
    or: 'or',
  },
  errors: {
    team_not_empty: 'The team still holds projects, boards (the trash too), pages, sprints or other spaces: move or delete them first',
    last_team: 'This is the organization’s last team',
    personal_team: 'A personal team cannot be deleted',
    team_not_in_project: 'This team does not work in the project',
    home_team: 'The project’s home team cannot be changed this way',
    other_org: 'This team is in another organization',
    personal_project: 'A personal project cannot be shared with other teams',
    invalid_space: 'The space belongs to another team or does not exist',
    invalid_points: 'Story points are a number from 0 to 1000',
    invalid_dates: 'The sprint ends before it starts',
    invalid_capacity: 'Capacity is a non-negative number',
    invalid_estimation: 'Unknown estimation',
    invalid_state: 'Not possible for a sprint in this state',
    invalid_move: 'Unknown way to move the sprint',
    ambiguous_key: 'Several of your organizations have an issue with this key — open it from its project',
    default_space: 'A team’s default space cannot be deleted',
    comment_exists: 'This comment already exists — reload the page',
    invalid_comment: 'The comment is empty or too long',
    invalid_anchor: 'Could not pin the comment to that place on the board',
    invalid_emoji: 'That reaction is not allowed',
    too_many_reactions: 'This message already has too many different reactions',
    comments_moved: 'Comments have moved — reload the page',
    too_many_spaces: 'The team has too many spaces',
    invalid_icon: 'This icon will not do — pick one from the list or upload a picture',
    icon_too_big: 'The picture is too big',
    guests_off: 'The organization does not allow inviting people from outside',
    not_a_guest: 'This is a member of the organization, not a guest',
    csv_unreadable: 'Could not read the file as a CSV table',
    no_rows: 'The table has no rows with issues',
    table_too_large: 'The table is too large: up to 50,000 rows and 300 columns',
    title_column_required: 'Choose the column with issue titles',
    invalid_role: 'Unknown role',
    team_admin: 'Team owners and admins always run the project',
    invalid_frequency: 'Unknown email frequency',
    invalid_minutes: 'Time is 1 minute to a week',
    invalid_date: 'Invalid date',
    invalid_period: 'The period is up to a quarter',
    note_too_long: 'The note is too long',
    invalid_wip_limit: 'The limit is 1 to 999',
    consent_required: 'Accept the terms and give your consent to the processing of personal data',
    confirm_mismatch: 'Type the account email to confirm',
    invalid_repo_url: 'Use a repository address like https://github.com/team/project.',
    invalid_provider: 'Choose GitHub, GitLab, Gitea or Bitbucket.',
    repo_exists: 'This repository is connected already.',
    too_many_repos: 'The team already has 100 repositories.',
    invalid_query: 'The query is empty or too long.',
    invalid_team: 'Choose a team you are in.',
    too_many_filters: 'You already have 100 filters: delete some.',
    too_many: 'Up to 500 issues can be changed at once.',
    file_too_large: 'The file is larger than 25 MB.',
    too_many_attachments: 'The issue already has 200 files.',
    length_required: 'The browser did not say how large the file is. Try again.',
    attachment_exists: 'This file is attached already.',
    transition_not_allowed: 'This status cannot be entered from the current one: the project is set up that way.',
    required_assignee: 'This status needs an assignee.',
    required_estimate: 'This status needs an estimate.',
    required_due: 'This status needs a due date.',
    required_description: 'This status needs a description.',
    required_labels: 'This status needs at least one label.',
    required_sprint: 'This status needs the issue to be in a sprint.',
    last_status: 'The last status cannot be deleted.',
    last_done_status: 'A project needs at least one status in the Done category.',
    move_to_required: 'Choose where the issues of this status go.',
    status_exists: 'A status with this name already exists.',
    too_many_statuses: 'A project can have at most 30 statuses.',
    invalid_color: 'Invalid color.',
    invalid_order: 'The statuses have changed: reload the page.',
    invalid_status: 'No such status in the project.',
    invalid_required_field: 'This field cannot be required.',
    invalid_link: 'This link cannot be made: check the issue.',
    too_many_links: 'The issue has too many links.',
    import_invalid_url: 'Check the address: it should be the site you are moving from (Jira, YouTrack, Confluence).',
    import_blocked_address: 'This address is in a private network, which Vatman does not reach.',
    import_unauthorized: 'Could not sign in: check the address, email and token.',
    import_forbidden: 'The token has no access to this.',
    import_not_found: 'Not found: check the address and the project or space.',
    import_rate_limited: 'The server asks to slow down. Try again in a minute.',
    import_timeout: 'The server takes too long to answer. Try again.',
    import_network: 'Could not connect to the server.',
    import_bad_response: 'The server answered unexpectedly. Check the address and the system chosen.',
    import_running: 'Two imports are running already. Wait for them to finish.',
    import_already_running: 'This import is running already — wait for it to finish.',
    invalid_archive: 'This is not a ZIP archive, or it is damaged.',
    archive_too_large: 'The archive is too large: up to 512 MB and 20,000 files.',
    invalid_system: 'This system is not supported here.',
    too_many_labels: 'A page can have at most 50 labels.',
    key_taken: 'Another project already has this key.',
    interrupted: 'The import was interrupted. Run it again: it continues without duplicates.',
    internal: 'Something went wrong. Please try again.',
    unauthorized: 'Please sign in.',
    forbidden: "You don't have permission to do that.",
    not_found: 'Not found.',
    too_many_requests: 'Too many attempts. Please wait a bit.',
    invalid_email: 'Please check the email address.',
    weak_password: 'Use at least 8 characters.',
    invalid_name: 'Please enter a name.',
    email_taken: 'This email is already registered. Sign in or reset your password.',
    invalid_credentials: 'Wrong email or password.',
    invalid_token: 'This link has expired or was already used.',
    email_not_verified: 'Please confirm your email first.',
    last_owner: "You can't remove the last owner.",
    domain_not_owned: 'You can only add the domain of your own verified email.',
    invalid_domain: 'Invalid domain.',
    not_in_org: "This person isn't in the organization.",
    oauth_failed: "Couldn't sign in with that provider.",
    telegram_unavailable: 'Telegram is not responding — try again a bit later.',
    oauth_no_email: "The provider didn't share an email address.",
    unsupported_type: 'This file type is not supported.',
    no_org: "This board doesn't belong to an organization.",
    use_transfer: 'Use ownership transfer to change the owner.',
    template_too_large: 'The template is too large: up to 5,000 items and 2 MB.',
    empty_template: 'The template has no items.',
    invalid_category: 'Pick a category.',
    invalid_scope: 'Pick who can use the template.',
    description_too_long: 'The description is longer than 1,000 characters.',
    invalid_access: 'Choose what the token may do.',
    invalid_expiry: 'Invalid expiry.',
    too_many_tokens: 'Too many tokens: revoke the ones you no longer use.',
    invalid_boards: 'Pick boards you have access to.',
  },
  legal: {
    acceptTerms: 'I accept the <terms>terms of service</terms> and have read the <privacy>privacy policy</privacy>',
    acceptPd: 'I give my <consent>consent to the processing of personal data</consent>',
    gateTitle: 'Accept the terms',
    gateText: 'To keep using Vatman, accept the terms of service and give your consent to the processing of personal data.',
    accept: 'Continue',
    deleteMenu: 'Delete account',
    deleteTitle: 'Delete your account?',
    deleteGone: 'Your email, name, password, Yandex and VK sign-ins, agent tokens, notifications and your personal space with all its boards, issues and files will be deleted.',
    deleteStay: 'Boards and projects of shared teams stay with the team: another member becomes owner. History, comments and issues will show “Deleted user” instead of your name.',
    deleteFinal: 'This cannot be undone. The email can be used for a new account again.',
    deletePolicy: 'See the <privacy>privacy policy</privacy> for details.',
    deleteConfirm: 'To confirm, type {{email}}',
    deleteButton: 'Delete forever',
  },
  auth: {
    loginTitle: 'Sign in to Vatman',
    registerTitle: 'Create your account',
    email: 'Work email',
    password: 'Password',
    name: 'Name',
    namePlaceholder: 'What should we call you',
    login: 'Sign in',
    register: 'Sign up',
    noAccount: 'No account yet?',
    haveAccount: 'Already have an account?',
    forgot: 'Forgot password?',
    magic: 'Email me a sign-in link',
    magicSent: 'We sent a sign-in link to {{email}}. It expires in 15 minutes.',
    forgotTitle: 'Reset password',
    forgotHint: "We'll email you a link to set a new password.",
    forgotSent: "If this email is registered, it's on its way.",
    send: 'Send',
    resetTitle: 'New password',
    resetSave: 'Save password',
    withYandex: 'Continue with Yandex ID',
    withVk: 'Continue with VK ID',
    verifying: 'Confirming your email…',
    verified: 'Email confirmed',
    verifyBanner: 'Confirm {{email}} to invite colleagues.',
    resend: 'Resend email',
    resent: 'Email sent',
    signingIn: 'Signing in…',
    logout: 'Sign out',
    terms: 'By signing up you agree to the terms of service and the privacy policy.',
    heroTitle: 'Think together.\nOn one canvas.',
    heroText: 'Sticky notes, diagrams, retros and planning — in real time, with up to a hundred people on one board.',
  },
  favorites: {
    title: 'Favorites',
    add: 'Add to favorites',
    remove: 'Remove from favorites',
  },
  nav: {
    newBoard: 'New board',
    newPage: 'New page',
    newProject: 'New project',
    openNewTab: 'Open in a new tab',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    rename: 'Rename',
    renamePage: 'Rename page',
    renameProject: 'Rename project',
    hide: 'Hide menu',
    show: 'Show menu',
    resize: 'Menu width: drag, double-click for the usual one',
    home: 'All boards',
    recent: 'Recent',
    starred: 'Starred',
    mine: 'My boards',
    shared: 'Shared with me',
    trash: 'Trash',
    teams: 'Teams',
    projects: 'Projects',
    personal: 'Personal',
    newOrg: 'Create organization',
    orgSettings: 'Organization settings',
    profile: 'Profile',
    language: 'Language',
    theme: 'Theme',
    light: 'Light',
    dark: 'Dark',
    system: 'System',
    menu: 'Menu',
    members: 'Members',
    importMiro: 'Import boards from Miro',
    importIssues: 'Import issues (Jira, YouTrack…)',
    importDocs: 'Import documents (Confluence, Notion…)',
    starredRecent: 'Starred and recent',
    everything: 'Everything in the organization',
    everythingPersonal: 'All boards and projects',
    recentProjects: 'Recent projects',
    recentBoards: 'Recent boards',
  },
  dashboard: {
    newBoard: 'New board',
    searchBoards: 'Search boards',
    startWith: 'Start from a template',
    empty: 'No boards here yet',
    emptyHint: 'Create your first board or start from a template.',
    emptyTrash: 'Trash is empty',
    trashHint: 'Deleted boards stay here until you delete them forever.',
    noResults: 'Nothing found',
    edited: 'Edited {{time}}',
    owner: 'Owner: {{name}}',
    star: 'Star',
    unstar: 'Unstar',
    duplicate: 'Duplicate',
    moveTo: 'Move to project',
    noProject: 'No project',
    toTrash: 'Move to trash',
    restore: 'Restore',
    purge: 'Delete forever',
    purgeConfirm: 'The board will be deleted permanently.',
    newProject: 'New project',
    projectName: 'Project name',
    newTeam: 'New team',
    chip: { all: 'All boards', mine: 'My boards', shared: 'Shared with me', projects: 'Projects' },
    noStarred: 'Star a board and it shows up here.',
    teamName: 'Team name',
    joinTeam: 'Join',
    allTeams: 'All teams in the organization',
    sortBy: 'Sort',
    sortUpdated: 'Last edited',
    sortName: 'Name',
    grid: 'Grid',
    list: 'List',
    joinOrgs: 'Your colleagues are already on Vatman',
    joinOrg: 'Join',
    boardsCount_one: '{{count}} board',
    boardsCount_few: '{{count}} boards',
    boardsCount_many: '{{count}} boards',
    boardsCount_other: '{{count}} boards',
  },
  org: {
    createTitle: 'New organization',
    createHint: 'An organization brings your company together: one member directory, teams, and boards shared with everyone in the organization.',
    name: 'Organization name',
    namePlaceholder: 'For example, Slicetech',
    members: 'Organization members',
    invitePlaceholder: 'Emails, separated by commas or new lines',
    invite: 'Invite',
    pending: 'Pending invitations',
    domains: 'Join by email domain',
    domainsHint: 'People with a verified email on this domain can join on their own.',
    teams: 'Teams',
    roles: { owner: 'Owner', admin: 'Admin', member: 'Member' },
    invited: 'Invitations sent',
    leave: 'Leave organization',
  },
  roles: {
    owner: 'Owner',
    editor: 'Can edit',
    commenter: 'Can comment',
    viewer: 'Can view',
    none: 'No access',
  },
  search: {
    placeholder: 'Search boards and content',
    titleAndContent: 'Found: {{count}}',
    inItems: 'On the board',
    inComments: 'In comments',
    moreItems_one: '{{count}} more match',
    moreItems_other: '{{count}} more matches',
    moreItems_few: '{{count}} more matches',
    moreItems_many: '{{count}} more matches',
    noResults: 'Nothing found',
    noResultsHint: 'Search covers board titles, text on boards and comments. Try another word.',
    itemNotFound: 'Item not found — it may have been deleted.',
  },
  notifications: {
    modeHint: 'When to send',
    mode: { instant: 'Right away', hourly: 'Hourly digest', daily: 'Daily digest at 09:00 Moscow time' },
    title: 'Notifications',
    open: 'Notifications',
    unread_one: '{{count}} unread',
    unread_other: '{{count}} unread',
    unread_few: '{{count}} unread',
    unread_many: '{{count}} unread',
    markAllRead: 'Mark all as read',
    empty: 'Nothing here yet',
    emptyHint: 'Mentions, replies in your discussions and invitations will show up here.',
    more: 'Show more',
    mention: '{{name}} mentioned you on “{{board}}”',
    reply: '{{name}} replied in a discussion on “{{board}}”',
    board_shared: '{{name}} shared “{{board}}” with you',
    team_invite: '{{name}} added you to the team “{{team}}”',
    org_invite: '{{name}} added you to the organization “{{org}}”',
    project_shared: '{{name}} invited you to the project “{{project}}” ({{role}})',
    issue_assigned: '{{name}} assigned {{issue}} “{{title}}” to you',
    issue_commented: '{{name}} commented on {{issue}} “{{title}}”',
    issue_status: '{{name}} moved {{issue}} “{{title}}” to “{{status}}”',
    issue_due: '{{issue}} “{{title}}”: {{when}}',
    issue_mention: '{{name}} mentioned you in {{issue}} “{{title}}”',
    page_mention: '{{name}} mentioned you on the page “{{page}}”',
    page_comment: '{{name}} commented on the page “{{page}}”',
    page_review_requested: '{{name}} asks you to approve “{{page}}”',
    page_review_decided: '{{name}} approved “{{page}}”',
    page_review_changes: '{{name}} asks for changes to “{{page}}”',
    page_review_done: '{{name}} completed the approval: “{{page}}” is approved',
    page_review_due: 'Approval of “{{page}}” is due {{when}}',
    space_post: '{{name}} posted “{{page}}” in “{{space}}”',
    due: { overdue: 'overdue', today: 'due today', tomorrow: 'due tomorrow', later: 'due soon' },
    unknown: 'New notification',
    emails: 'Email notifications',
    emailHint: 'In-app notifications always arrive. Choose which ones are also sent by email.',
    email: {
      mention: 'Mentions',
      reply: 'Replies in discussions',
      share: 'Board access',
      invite: 'Team invitations',
      issues: 'Issues: assignments, comments, statuses, due dates',
      reviews: 'Approval of documents',
      news: 'News of spaces',
    },
    telegram: {
      title: 'Telegram',
      hint: 'Notifications will come to the chat with @{{bot}}.',
      hintNoBot: 'Notifications will come to the chat with the Vatman bot.',
      connect: 'Connect Telegram',
      opened: 'In Telegram, press «Start» — the bot connects by itself.',
      linked: 'Connected: @{{name}}',
      linkedNoName: 'Telegram is connected',
      groupsHint: 'What to send to Telegram',
      disconnect: 'Disconnect Telegram',
      disconnected: 'Telegram disconnected',
    },
  },
  access: {
    none: 'No access',
    view: 'Can view',
    comment: 'Can comment',
    edit: 'Can edit',
  },
  share: {
    title: 'Share board',
    invitePlaceholder: 'Email or colleague name',
    linkAccess: 'Anyone with the link',
    teamAccess: 'Team “{{name}}”',
    spaceAccess: 'Space “{{name}}”',
    spaceHint: 'Each up to their role in the space.',
    privateSpaceHint: 'The space is private: the organization does not see the board.',
    orgAccess: 'Everyone in “{{name}}”',
    copyLink: 'Copy link',
    people: 'People with access',
    pending: 'Invitation sent',
    transfer: 'Make owner',
    onlyOwner: 'Only the board owner can change access settings.',
    linkHint: 'No account needed — just the link.',
  },
  board: {
    loading: 'Opening board…',
    notFound: "This board doesn't exist or you don't have access",
    notFoundHint: 'Ask the owner to share it with you or check the link.',
    deleted: 'This board was deleted',
    toDashboard: 'Back to boards',
    readOnly: 'View only',
    offline: "You're offline — changes will sync later",
    connecting: 'Connecting…',
    online: 'All changes saved',
    signInToEdit: 'Sign in to edit',
    share: 'Share',
    present: 'Present',
    frames: 'Frames',
    noFrames: 'Add frames — they become presentation slides.',
    following: 'Following {{name}}',
    stopFollowing: 'Stop following',
    follow: 'Follow {{name}}',
    summon: 'Bring everyone to me',
    summoned: 'Everyone was brought to you',
    frame: 'Frame',
    emptyTitle: 'Blank canvas',
    emptyHint: 'Double-click to add a sticky note, or pick a template.',
    pickTemplate: 'Pick a template',
    uploading: 'Uploading image…',
    uploadFailed: "Couldn't upload the image",
    exportPng: 'Export as PNG',
    exportPdf: 'Export frames as PDF',
    exportingPdf: 'Preparing PDF…',
    nothingToExport: 'Nothing to export yet',
    exportJson: 'Download a copy (.json)',
    importJson: 'Import from file (.json)',
    duplicateBoard: 'Duplicate board',
    boardCopied: 'Board copy created',
    shortcuts: 'Keyboard shortcuts',
    commands: 'Commands',
    grid: 'Grid',
    theme: 'Theme',
    motion: 'Animations',
    motionOn: 'On',
    motionOff: 'Off',
    motionSystem: 'Match system',
    rename: 'Rename board',
    undo: 'Undo',
    redo: 'Redo',
    menu: 'Board menu',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    zoomFit: 'Fit to screen',
    zoomReset: 'Zoom to 100%',
    minimap: 'Minimap',
    help: 'Help',
  },
  tools: {
    select: 'Select',
    hand: 'Hand',
    sticky: 'Sticky note',
    shape: 'Shapes',
    text: 'Text',
    connector: 'Connector',
    pen: 'Pen',
    highlighter: 'Highlighter',
    eraser: 'Eraser',
    frame: 'Frame',
    image: 'Image',
    comment: 'Comment',
    card: 'Task card',
    stamp: 'Stamp',
    laser: 'Laser pointer',
    templates: 'Templates',
    more: 'More',
  },
  shapes: {
    rect: 'Rectangle',
    rounded: 'Rounded',
    ellipse: 'Ellipse',
    triangle: 'Triangle',
    diamond: 'Diamond',
    hexagon: 'Hexagon',
    parallelogram: 'Parallelogram',
    star: 'Star',
    cylinder: 'Cylinder',
    callout: 'Callout',
    arrow: 'Arrow',
  },
  cards: {
    title: 'Task',
    untitled: 'Untitled',
    titlePlaceholder: 'Task title',
    descriptionPlaceholder: 'Add a description…',
    status: 'Status',
    statuses: { todo: 'To do', doing: 'In progress', done: 'Done' },
    assignee: 'Assignee',
    unassigned: 'Unassigned',
    searchPeople: 'Find a board member',
    noPeople: 'Nobody found',
    peopleSignIn: 'Sign in to assign tasks.',
    due: 'Due date',
    clearDue: 'Clear due date',
    overdue: 'Overdue',
    tags: 'Tags',
    addTag: 'Add a tag',
    removeTag: 'Remove tag “{{tag}}”',
    color: 'Color',
    details: 'Open task',
    zoomTo: 'Show on the board',
    delete: 'Delete card',
    convert: 'Convert to cards',
    converted: 'Cards made from stickies: {{count}}',
    issue: 'Issue',
    openIssue: 'Open in the tracker',
    unlinkIssue: 'Unlink from the issue',
    createIssue: 'Create',
    linkIssue: 'Link',
    pickProject: 'In which project',
    noProjects: 'No projects where you can create issues. Create one under Tasks.',
    searchIssues: 'Key or words from the title',
    issueCreated: '{{key}} created and linked to the card',
    issueLinked: 'The card is linked to {{key}}',
    syncFailed: '{{key}} was not updated: {{error}}',
    toTracker: 'Create issues in the tracker…',
    toTrackerTitle: 'Issues from the selection',
    toTrackerHint: 'Stickies become cards, and every card gets an issue. From then on they change together, on the board and in the tracker.',
    project: 'Project',
    sprint: 'Sprint',
    toTrackerCreate_one: 'Create {{count}} issue',
    toTrackerCreate_few: 'Create {{count}} issues',
    toTrackerCreate_many: 'Create {{count}} issues',
    toTrackerCreate_other: 'Create {{count}} issues',
    toTrackerDone_one: '{{count}} issue created in “{{project}}”',
    toTrackerDone_few: '{{count}} issues created in “{{project}}”',
    toTrackerDone_many: '{{count}} issues created in “{{project}}”',
    toTrackerDone_other: '{{count}} issues created in “{{project}}”',
    toTrackerPartial_one: '{{count}} issue was created before that.',
    toTrackerPartial_few: '{{count}} issues were created before that.',
    toTrackerPartial_many: '{{count}} issues were created before that.',
    toTrackerPartial_other: '{{count}} issues were created before that.',
    myTasks: 'My tasks',
    myTasksEmpty: 'Nothing is assigned to you on this board yet.',
    myTasksSignIn: 'Sign in to see your tasks.',
  },
  props: {
    color: 'Color',
    fill: 'Fill',
    stroke: 'Stroke',
    text: 'Text',
    size: 'Size',
    auto: 'Auto',
    align: 'Alignment',
    bold: 'Bold',
    italic: 'Italic',
    shape: 'Shape',
    lineStyle: 'Line style',
    solid: 'Solid',
    dashed: 'Dashed',
    dotted: 'Dotted',
    width: 'Width',
    route: 'Line type',
    straight: 'Straight',
    elbow: 'Elbow',
    curve: 'Curved',
    startCap: 'Start',
    endCap: 'End',
    lock: 'Lock',
    unlock: 'Unlock',
    front: 'Bring to front',
    back: 'Send to back',
    duplicate: 'Duplicate',
    delete: 'Delete',
    copy: 'Copy',
    paste: 'Paste',
    cut: 'Cut',
    alignLeft: 'Align left',
    alignCenter: 'Align center',
    alignRight: 'Align right',
    alignTop: 'Align top',
    alignMiddle: 'Align middle',
    alignBottom: 'Align bottom',
    distributeH: 'Distribute horizontally',
    distributeV: 'Distribute vertically',
    arrange: 'Arrange',
    frameIt: 'Wrap in a frame',
    selectAll: 'Select all',
    zoomTo: 'Zoom to selection',
    transparent: 'No fill',
    editText: 'Edit text',
    addLabel: 'Add label',
    swap: 'Swap ends',
  },
  palette: {
    inVatman: 'In Vatman',
    placeholder: 'Command or text on the board…',
    noResults: 'Nothing found',
    actions: 'Actions',
    onBoard: 'On the board',
    frames: 'Frames',
  },
  shortcutsGroups: {
    tools: 'Tools',
    edit: 'Editing',
    view: 'View',
    create: 'Quick create',
    mindmap: 'Mind map',
  },
  shortcutsList: {
    pan: 'Pan the canvas',
    zoom: 'Zoom',
    fit: 'Fit to screen',
    zoomSel: 'Zoom to selection',
    zoom100: 'Zoom to 100%',
    undo: 'Undo',
    redo: 'Redo',
    duplicate: 'Duplicate',
    group: 'Wrap in a frame',
    delete: 'Delete',
    nudge: 'Nudge by 1 / 10 px',
    edit: 'Edit text',
    sibling: 'Connected sticky in a direction',
    dblclick: 'Double-click — new sticky',
    palette: 'Command palette',
    lock: 'Lock',
    order: 'Bring to front / back',
    selectAll: 'Select all',
    nextSticky: 'Next sticky (while typing)',
    mindChild: 'Subtopic',
    mindSibling: 'Next topic',
    mindPromote: 'Move a level up',
    mindCollapse: 'Collapse / expand a branch',
    mindDelete: 'Delete a branch',
    mindNavigate: 'Move between topics',
    mindReorder: 'Reorder topics',
    mindEdit: 'Edit text (or just start typing)',
  },
  mindmap: {
    new: 'New mind map',
    root: 'Central topic',
    addChild: 'Add subtopic',
    collapse: 'Collapse branch',
    expand: 'Expand branch',
    branchColor: 'Branch color',
    copyMarkdown: 'Copy as Markdown outline',
    copied: 'Outline copied as Markdown',
  },
  templates: {
    blank: 'Blank board',
    brainstorm: 'Brainstorm',
    retro: 'Retrospective',
    kanban: 'Kanban',
    mindmap: 'Mind map',
    swot: 'SWOT analysis',
    journey: 'Customer journey',
    content: {
      brainstormTopic: 'Key question',
      idea: 'Idea',
      retroGood: 'What went well',
      retroBad: 'What could be better',
      retroActions: 'Action items',
      retroTitle: 'Sprint retrospective',
      backlog: 'Backlog',
      todo: 'To do',
      doing: 'In progress',
      review: 'Review',
      done: 'Done',
      task: 'Task',
      centralTopic: 'Central topic',
      branch: 'Branch',
      strengths: 'Strengths',
      weaknesses: 'Weaknesses',
      opportunities: 'Opportunities',
      threats: 'Threats',
      stages: ['Discover', 'Research', 'Buy', 'Use', 'Recommend'],
      actions: 'Customer actions',
      feelings: 'Feelings',
      pains: 'Pain points',
      ideas: 'Improvement ideas',
    },
  },
  teamTemplates: {
    builtIn: 'Built-in',
    tab: 'Team templates',
    saveBoard: 'Save board as template',
    saveSelection: 'Save selection as template',
    name: 'Name',
    namePlaceholder: 'For example, “Our team retro”',
    description: 'Description',
    descriptionPlaceholder: 'When and how to use this template',
    category: 'Category',
    scope: 'Available to',
    teams: 'Teams',
    orgs: 'Organizations',
    personal: 'Only me (personal space)',
    wholeOrg: 'Everyone in “{{name}}”',
    items_one: '{{count}} item',
    items_few: '{{count}} items',
    items_many: '{{count}} items',
    items_other: '{{count}} items',
    save: 'Save template',
    saved: 'Template “{{name}}” saved',
    nothing: 'Nothing to save: the board is empty.',
    tooLarge: 'The template is too large: up to 5,000 items and 2 MB.',
    empty: 'No templates yet. Save a board or a selection from the board menu, and it shows up here for the whole team.',
    noResults: 'Nothing found',
    search: 'Search templates',
    all: 'All',
    edit: 'Edit',
    editTitle: 'Template',
    delete: 'Delete',
    deleteTitle: 'Delete this template?',
    deleteConfirm: 'Template “{{name}}” will be gone for everyone. Boards created from it stay.',
    deleted: 'Template deleted',
    by: 'By {{name}}',
    loading: 'Loading template…',
    categories: {
      retro: 'Retro',
      planning: 'Planning',
      research: 'Research',
      strategy: 'Strategy',
      education: 'Education',
      other: 'Other',
    },
  },
  comments: {
    refused: 'The server rejected a change to the comments. Comments were reloaded.',
    title: 'Comments',
    thread: 'Comment',
    placeholder: 'Write a comment… @ to mention',
    replyPlaceholder: 'Reply…',
    send: 'Send',
    resolve: 'Resolve',
    reopen: 'Reopen',
    resolvedBy: 'Resolved by {{name}}',
    openTab: 'Open',
    resolvedTab: 'Resolved',
    deleteThread: 'Delete thread',
    deleteMessage: 'Delete message',
    replies_one: '{{count}} reply',
    replies_few: '{{count}} replies',
    replies_many: '{{count}} replies',
    replies_other: '{{count}} replies',
    show: 'Show comments on the board',
    hide: 'Hide comments on the board',
    empty: 'No comments yet. Press C and click the board to leave one.',
    emptyResolved: 'No resolved comments.',
    notified: 'Mentioned people notified: {{n}}',
    notFound: 'Comment not found — it may have been deleted.',
    noPeople: 'No matches',
    mentionsSignIn: 'Sign in to mention people',
    reply: 'Reply',
    edit: 'Edit',
    edited: 'edited',
    editHint: 'Enter saves, Esc cancels',
    react: 'React',
    deletedMessage: 'Message deleted',
    imported: 'not on Vatman',
  },
  timer: {
    title: 'Timer',
    start: 'Start',
    pause: 'Pause',
    resume: 'Resume',
    addMinute: '1 min',
    reset: 'Reset',
    min: 'min',
    custom: 'Custom, minutes',
    done: "Time's up",
    setBy: 'Started by {{name}}',
    onlyEditors: 'Board editors control the timer.',
  },
  voting: {
    title: 'Voting',
    start: 'Start voting',
    votesPerPerson: 'Votes per person',
    startHint: "Participants click sticky notes to vote. Others' votes stay hidden until voting ends.",
    left: 'Votes left: {{n}} of {{total}}',
    voted: 'Voted: {{n}}',
    done: 'Done',
    back: 'Change votes',
    youDone: "You've voted",
    end: 'End voting',
    results: 'Voting results',
    close: 'Close voting',
    finished: 'Voting has ended',
    noLeft: 'No votes left. Alt+click a sticky to take a vote back.',
    takeBack: 'Take the vote back',
    summary: 'Participants: {{voters}} · votes: {{votes}}',
    inProgress: 'Voting is still in progress. Results appear when it ends.',
    noResults: 'There has been no voting on this board yet.',
    noVotes: 'Nobody voted.',
    emptySticky: 'Empty sticky note',
  },
  quickOpen: {
    button: 'Find',
    title: 'Search Vatman',
    placeholder: 'An issue, board, project or space — or a key like PAY-12',
    issues: 'Issues',
    boards: 'Boards',
    projects: 'Projects',
    spaces: 'Spaces',
    pages: 'Pages',
    goTo: 'Go to',
    inComment: 'in a comment',
  },
  create: {
    title: 'Create',
    board: 'Board',
    page: 'Page',
    issue: 'Issue',
    sprint: 'Sprint',
    startSprint: 'Start a sprint',
    project: 'Project',
    space: 'Space',
    pick: { issue: 'New issue — in which project?', sprint: 'New sprint — in which project?', startSprint: 'Start a sprint — in which project?' },
    noProjects: 'There are no projects here where you can do this yet.',
    findProject: 'Find a project',
    alreadyRunning: 'Sprint “{{name}}” is already running',
  },
  docs: {
    titleLabel: 'Title',
    title: 'Documents',
    mode: { edit: 'Edit', done: 'Done' },
    importHere: 'Import here…',
    openOrigin: 'Open in Confluence',
    legacyNotFound: 'Page not found',
    legacyNotFoundHint: 'This page has not been moved from Confluence yet, or you have no access to it.',
    legacyHome: 'Go home',
    labels: {
      title: 'Labels',
      find: 'Find pages labelled “{{label}}”',
      remove: 'Remove the label “{{label}}”',
      placeholder: 'New label',
      add: 'Label',
    },
    untitled: 'Untitled',
    notFound: 'The page is not found or not available to you',
    path: 'Where the page is',
    offline: 'Offline: changes are kept and sent when the connection is back',
    addChild: 'Add a page inside',
    linkCopied: 'Link copied',
    copyLink: 'Copy link',
    exportMarkdown: 'Download as Markdown',
    exportPdf: 'Download PDF',
    exportDocx: 'Download DOCX (Word)',
    exportBranchPdf: 'PDF with every page inside',
    exportBranchDocx: 'DOCX with every page inside',
    exportBranch: 'Download with the pages inside (ZIP)',
    print: 'Print or PDF',
    delete: 'Move to trash',
    deleted: 'The page is in the trash',
    inTrash: 'This page is in the trash.',
    trashedHint: 'Restore the page to read and edit it.',
    deleteTitle: 'Delete the page?',
    deleteText: 'The page goes to the space’s trash; it can be restored for 30 days.',
    deleteWithChildren_one: 'The page goes to the trash with {{count}} page inside it; they can be restored for 30 days.',
    deleteWithChildren_few: 'The page goes to the trash with {{count}} pages inside it; they can be restored for 30 days.',
    deleteWithChildren_many: 'The page goes to the trash with {{count}} pages inside it; they can be restored for 30 days.',
    deleteWithChildren_other: 'The page goes to the trash with {{count}} pages inside it; they can be restored for 30 days.',
    icon: 'Page icon',
    addIcon: 'Icon',
    removeIcon: 'Remove icon',
    outline: 'On this page',
    edited: 'Edited by {{who}}, {{when}}',
    empty: 'No pages here yet',
    emptyHint: 'Pages are for policies, decisions, how-tos and anything the team needs written down.',
    newPage: 'New page',
    pagesUnder_one: '{{count}} page inside',
    pagesUnder_few: '{{count}} pages inside',
    pagesUnder_many: '{{count}} pages inside',
    pagesUnder_other: '{{count}} pages inside',
    trash_one: 'Trash: {{count}} page',
    trash_few: 'Trash: {{count}} pages',
    trash_many: 'Trash: {{count}} pages',
    trash_other: 'Trash: {{count}} pages',
    deletedBy: 'deleted by {{who}} {{when}}',
    noPages: 'No pages yet',
    noChildren: 'No pages inside yet.',
    tocEmpty: 'Add headings and they show up here.',
    blocks: { childPages: 'Pages inside', toc: 'Contents', issueList: 'Issues', board: 'Open the board' },
    callout: { change: 'Change the callout' },
    fileTooLarge: '“{{name}}” is larger than 25 MB',
    uploading: 'Uploading “{{name}}”…',
    placeholder: { heading: 'Heading', text: 'Write, or press “/” for blocks' },
    slash: {
      text: 'Text',
      h1: 'Heading 1',
      h2: 'Heading 2',
      h3: 'Heading 3',
      bullet: 'Bulleted list',
      ordered: 'Numbered list',
      task: 'Checklist',
      quote: 'Quote',
      callout: 'Callout',
      details: 'Toggle block',
      code: 'Code',
      table: 'Table',
      divider: 'Divider',
      file: 'Image or file',
      toc: 'Table of contents',
      children: 'Pages inside',
      issues: 'Issue list',
      board: 'Board',
      frame: 'Board frame',
      nothing: 'Nothing found',
    },
    format: { bold: 'Bold', italic: 'Italic', underline: 'Underline', strike: 'Strikethrough', code: 'Code', highlight: 'Highlight', link: 'Link' },
    comments: {
      title: 'Comments',
      add: 'Comment',
      onText: 'Comment on the selection',
      onPage: 'Comment on the page',
      none: 'No discussions yet. Select text to comment on it.',
      resolved_one: 'Resolved: {{count}}',
      resolved_few: 'Resolved: {{count}}',
      resolved_many: 'Resolved: {{count}}',
      resolved_other: 'Resolved: {{count}}',
      resolve: 'Resolve',
      reopen: 'Reopen',
    },
    history: {
      title: 'Version history',
      saved: 'Version saved',
      restored: 'Version restored',
      namePlaceholder: 'Version name, e.g. “Approved”',
      save: 'Save',
      none: 'No versions yet: they appear after edits.',
      restore: 'Restore this version',
      reason: { review: 'Sent for approval', approved: 'Approved' },
    },
    news: {
      title: 'News',
      new: 'New post',
      draft: 'draft',
      drafts_one: '{{count}} draft',
      drafts_few: '{{count}} drafts',
      drafts_many: '{{count}} drafts',
      drafts_other: '{{count}} drafts',
      empty: 'No news yet',
      emptyHint: 'Tell the team about a release, a decision or an event — members of the space are notified.',
      draftHint: 'A draft: its author and the space’s editors see it. Members hear about the post once it is published.',
      publish: 'Publish',
      published: 'The post is published',
      publishedAt: 'Published {{date}}',
      unpublish: 'Unpublish',
      unpublished: 'The post is a draft again',
    },
    filter: {
      all: 'All',
      review: 'Waiting for approval',
      approved: 'Approved',
      none: { review: 'Nothing is waiting for approval.', approved: 'No approved pages yet.' },
    },
    review: {
      menu: 'Send for approval…',
      sendTitle: 'Send for approval',
      sendHint: 'Approvers get a notification and an email. The approved text is kept in the history.',
      findPeople: 'Find a person',
      rule: { all: 'Everyone must approve', any: 'One is enough' },
      dueLabel: 'Due date (optional)',
      message: 'Message to approvers',
      send_one: 'Send to {{count}} person',
      send_few: 'Send to {{count}} people',
      send_many: 'Send to {{count}} people',
      send_other: 'Send to {{count}} people',
      sent: 'The page is sent for approval',
      waiting: 'Waiting for approval by {{names}}',
      due: 'due {{date}}',
      anyOne: 'one is enough',
      sentBy: 'Sent by {{name}}',
      approve: 'Approve',
      changes: 'Request changes',
      commentPlaceholder: 'What should change?',
      sendChanges: 'Send',
      sinceSent: 'What changed since sent',
      withdraw: 'Withdraw',
      changesNeeded: 'Changes requested',
      sendAgain: 'Send again',
      approved: 'Approved {{date}}',
      changedSince: 'Edited since its approval on {{date}}',
      whatChanged: 'What changed',
      changesTitle: 'What changed',
      badge: { review: 'Waiting for approval', changes: 'Changes requested', approved: 'Approved', changed: 'Edited since approval' },
    },
    diff: {
      mode: { version: 'Version', changes: 'Changes', current: 'Against now' },
      summary: 'Words added: {{added}} · removed: {{removed}} · blocks touched: {{blocks}}',
      none: 'No changes',
      count: 'Changes: {{count}}',
      position: '{{at}} of {{count}}',
      previous: 'Previous change',
      next: 'Next change',
    },
    access: {
      title: 'Page access',
      closed: 'The page is closed',
      inherited: 'Access is set on a page above:',
      mode: { open: 'As the space', edit: 'Only some people edit', view: 'Only some people see' },
      hint: {
        open: 'Everyone the space allows reads and edits.',
        edit: 'Everyone in the space reads; only those checked edit.',
        view: 'Others in the space do not see this page and the pages under it.',
      },
      whoReads: 'Who sees it',
      whoEdits: 'Who edits it',
      canEdit: 'edits',
      adminsNote: 'Space admins see and edit every page.',
    },
    templates: {
      start: 'Start from a template',
      remove: 'Delete template',
      saved: 'Template saved',
      saveTitle: 'Save as template',
      name: 'Template name',
      description: 'What it is for',
      for: 'For',
      team: 'The team',
      org: 'The whole organization',
    },
    backlinks_one: '{{count}} page or issue links here',
    backlinks_few: '{{count}} pages or issues link here',
    backlinks_many: '{{count}} pages or issues link here',
    backlinks_other: '{{count}} pages or issues link here',
    links: { typeMore: 'Type at least two letters', nothing: 'Nothing found' },
    issueList: {
      change: 'Change query',
      first_one: 'Showing the first {{count}} issue',
      first_few: 'Showing the first {{count}} issues',
      first_many: 'Showing the first {{count}} issues',
      first_other: 'Showing the first {{count}} issues',
    },
    board: {
      pick: 'Pick a board',
      noAccess: 'The board is not available',
      pickFrame: 'Pick a frame',
      noFrames: 'The board has no frames — add one on the board.',
      frameGone: 'The frame was removed from the board',
      untitledFrame: 'Untitled frame',
      open: 'Open on the board',
    },
  },
  inbox: {
    title: 'Inbox',
    tab: { all: 'All', unread: 'Unread', discussions: 'Discussions', issues: 'Issues', access: 'Access' },
    deadlines: 'Due soon',
    allRead: 'All caught up',
    emptyHint: 'Mentions, comments and status changes on your issues, assignments, due dates and invitations show up here.',
  },
  modules: {
    menu: 'Menu',
    label: 'Sections',
    boards: 'Boards',
    tasks: 'Tasks',
  },
  docsImport: {
    title: 'Import documents',
    hint: 'Move a Confluence space, Notion pages or a Markdown archive here — with pictures, links and comments.',
    menu: 'Import documents…',
    fromConfluence: 'Import from Confluence or Markdown',
    source: {
      confluence_cloud: 'Confluence Cloud',
      confluence_server: 'Data Center',
      notion: 'Notion',
      markdown: 'Markdown',
    },
    url: 'Confluence address',
    urlPlaceholder: {
      confluence_cloud: 'https://company.atlassian.net',
      confluence_server: 'https://wiki.company.com',
    },
    tokenHelp: {
      confluence_cloud: 'Create an API token at id.atlassian.com → Security → API tokens. It needs read access to the space.',
      confluence_server: 'A personal access token: avatar → Profile → Personal Access Tokens (Confluence 7.9 and later).',
      notion: 'Create an internal integration at notion.so/profile/integrations (read content, comments and user information with emails), copy its secret and give it access to the pages: “…” → “Connections”.',
    },
    notionPick: 'What to move',
    notionNothing: 'No page is shared with the integration — add it under “Connections” of the pages.',
    database: 'database',
    markdownHint: 'Notion: Settings → Export → Markdown & CSV. Obsidian and GitHub Wiki: the folder of .md files in a ZIP. Folders become nested pages, pictures become files.',
    chooseZip: 'Choose a ZIP archive',
    findSpace: 'Find a space',
    noSpaces: 'No spaces found — check the token’s access.',
    under: 'Inside the page “{{title}}”',
    again: 'Importing the same space again catches up: changed pages are updated and new ones added, without duplicates.',
    start: 'Start import',
    reading: 'Reading the list of pages…',
    progress: '{{done}} of {{total}} moved',
    canClose: 'You can close this — the import goes on, and its result shows up here.',
    done: 'Done',
    created_one: '{{count}} new page',
    created_few: '{{count}} new pages',
    created_many: '{{count}} new pages',
    created_other: '{{count}} new pages',
    updated_one: '{{count}} page updated',
    updated_few: '{{count}} pages updated',
    updated_many: '{{count}} pages updated',
    updated_other: '{{count}} pages updated',
    unchanged_one: '{{count}} page unchanged',
    unchanged_few: '{{count}} pages unchanged',
    unchanged_many: '{{count}} pages unchanged',
    unchanged_other: '{{count}} pages unchanged',
    files_one: '{{count}} file',
    files_few: '{{count}} files',
    files_many: '{{count}} files',
    files_other: '{{count}} files',
    comments_one: '{{count}} comment',
    comments_few: '{{count}} comments',
    comments_many: '{{count}} comments',
    comments_other: '{{count}} comments',
    closed_one: '{{count}} page restricted as it was in Confluence',
    closed_few: '{{count}} pages restricted as they were in Confluence',
    closed_many: '{{count}} pages restricted as they were in Confluence',
    closed_other: '{{count}} pages restricted as they were in Confluence',
    keptDeleted_one: '{{count}} page deleted here — left in the trash',
    keptDeleted_few: '{{count}} pages deleted here — left in the trash',
    keptDeleted_many: '{{count}} pages deleted here — left in the trash',
    keptDeleted_other: '{{count}} pages deleted here — left in the trash',
    macros: 'Macros not moved (a note with a link to the original is in their place):',
    unmatched: 'People not found here — kept as names:',
    warnings_one: '{{count}} note',
    warnings_few: '{{count}} notes',
    warnings_many: '{{count}} notes',
    warnings_other: '{{count}} notes',
    open: 'Open',
  },
  trackerImport: {
    csvHint: 'A table from Yandex Tracker, Kaiten, Trello, Jira or Excel, headers in the first row. Columns are matched by name; you can correct them.',
    csvChoose: 'Choose a .csv file',
    csvRows_one: '{{count}} row',
    csvRows_few: '{{count}} rows',
    csvRows_many: '{{count}} rows',
    csvRows_other: '{{count}} rows',
    csvKey: 'Project key',
    csvName: 'Project name',
    csvKeyHint: 'Issues keep the numbers of the Key column (OPS-12 stays OPS-12). Loading the same table again updates them instead of making copies.',
    csvField: {
      title: 'Issue title',
      key: 'Key',
      description: 'Description',
      status: 'Status',
      type: 'Type',
      priority: 'Priority',
      assignee: 'Assignee',
      reporter: 'Reporter',
      labels: 'Labels',
      due: 'Due date',
      estimate: 'Estimate',
      created: 'Created',
      parent: 'Parent or epic',
      sprint: 'Sprint',
    },
    worklogs_one: '{{count}} worklog',
    worklogs_few: '{{count}} worklogs',
    worklogs_many: '{{count}} worklogs',
    worklogs_other: '{{count}} worklogs',
    entry: 'Import from Jira and others',
    title: 'Import issues',
    hint: 'The project moves with the same keys and issue numbers. The token is used only while importing and is never stored.',
    system: { jira_cloud: 'Jira Cloud', jira_server: 'Jira Data Center', youtrack: 'YouTrack', yandex_tracker: 'Yandex Tracker', kaiten: 'Kaiten', trello: 'Trello', csv: 'CSV file' },
    url: 'Address',
    urlPlaceholder: { jira_cloud: 'company.atlassian.net', jira_server: 'https://jira.company.com', youtrack: 'https://company.youtrack.cloud', yandex_tracker: '', kaiten: 'https://company.kaiten.ru', trello: '' },
    email: 'Atlassian account email',
    orgId: 'Organization ID',
    trelloKey: 'API key',
    token: 'Token',
    tokenHelp: {
      jira_cloud: 'API token: id.atlassian.com → Security → API tokens. Read access to the project is enough.',
      jira_server: 'Personal Access Token: your Jira profile → Personal Access Tokens.',
      youtrack: 'Permanent token: your YouTrack profile → Account Security → Tokens, with YouTrack access.',
      yandex_tracker: 'An OAuth token with tracker:read (oauth.yandex.ru) or an IAM token. The organization ID is in Tracker: Administration → Organizations; digits for Yandex 360, otherwise a Yandex Cloud organization.',
      kaiten: 'API key: your Kaiten profile → API key (/profile/api-key). Boards move over: columns, cards, comments and time logs; issues are numbered in order.',
      trello: 'API key and token: trello.com/power-ups/admin → your Power-Up → API key, then the “Token” link. Trello gives no emails, so people are matched by name.',
    },
    connect: 'Connect',
    findProject: 'Find a project',
    noProjects: 'No projects found.',
    team: 'Into which team',
    next: 'Next',
    issuesCount_one: '{{count}} issue',
    issuesCount_few: '{{count}} issues',
    issuesCount_many: '{{count}} issues',
    issuesCount_other: '{{count}} issues',
    keyState: {
      newNumbered: 'A new project {{key}} is created; issues are numbered in order and keep their numbers on the next import.',
      new: 'A new project {{key}} is created; issues keep their numbers.',
      again: '{{key}} was imported before: changed issues are updated and new ones added, without duplicates.',
      empty: 'Issues go into the empty project {{key}} the team already has.',
      taken: 'The key {{key}} belongs to another project with issues. Change its key or pick another team.',
    },
    statuses: 'Statuses',
    peopleMatched: 'Found in the team',
    peopleUnmatched: 'Not in the team',
    unmatchedHint: 'Their issues stay unassigned, with the original name shown on the issue. Invite them to the team and a repeated import assigns the issues.',
    start: 'Import',
    catchUp: 'Catch up on changes',
    progress: '{{done}} of {{total}} imported',
    phase: { start: 'Connecting…', statuses: 'Importing statuses…', issues: 'Importing issues…', links: 'Linking issues…' },
    canClose: 'You can close this window: the import goes on.',
    done: 'Done: {{key}} is in Vatman.',
    created_one: '{{count}} new issue',
    created_few: '{{count}} new issues',
    created_many: '{{count}} new issues',
    created_other: '{{count}} new issues',
    updated_one: '{{count}} updated',
    updated_few: '{{count}} updated',
    updated_many: '{{count}} updated',
    updated_other: '{{count}} updated',
    comments_one: '{{count}} comment',
    comments_few: '{{count}} comments',
    comments_many: '{{count}} comments',
    comments_other: '{{count}} comments',
    sprints_one: '{{count}} sprint',
    sprints_few: '{{count}} sprints',
    sprints_many: '{{count}} sprints',
    sprints_other: '{{count}} sprints',
    unmatchedDone: 'Not found in the team:',
    skipped_one: '{{count}} issue skipped:',
    skipped_few: '{{count}} issues skipped:',
    skipped_many: '{{count}} issues skipped:',
    skipped_other: '{{count}} issues skipped:',
    reason: { number_taken: 'the number is taken by an issue created in Vatman', deleted_here: 'deleted in Vatman' },
    files: 'Bring the issues’ files',
    filesHint: 'up to 50 MB each; takes longer',
    links_one: 'links: {{count}}',
    links_few: 'links: {{count}}',
    links_many: 'links: {{count}}',
    links_other: 'links: {{count}}',
    attachments_one: 'files: {{count}}',
    attachments_few: 'files: {{count}}',
    attachments_many: 'files: {{count}}',
    attachments_other: 'files: {{count}}',
    attachmentsSkipped_one: '{{count}} file was not brought over: larger than 50 MB, or the system would not give it. It stays there.',
    attachmentsSkipped_few: '{{count}} files were not brought over: larger than 50 MB, or the system would not give them. They stay there.',
    attachmentsSkipped_many: '{{count}} files were not brought over: larger than 50 MB, or the system would not give them. They stay there.',
    attachmentsSkipped_other: '{{count}} files were not brought over: larger than 50 MB, or the system would not give them. They stay there.',
    openProject: 'Open the project',
  },
  teams: {
    nameTitle: 'Team name',
    renameTitle: 'Rename team',
    renamed: 'Team renamed',
    delete: 'Delete team',
    deleteHint: 'Only an empty team can be deleted: no projects of its own, boards (in the trash too), pages, sprints, agents, templates or shared filters, and just its empty default space. It leaves other teams’ projects; its issues there go back to their home teams. An organization’s last team cannot be deleted.',
    deleteConfirm: 'Delete team “{{name}}”? This cannot be undone.',
    deleted: 'Team “{{name}}” deleted',
    backlogAll: 'All issues of the team’s projects',
    backlogOwn: 'Only the team’s issues',
    sprintsOf: 'Sprints are shared by all the team’s projects:',
    mine: 'My sprints',
    tab: { board: 'Sprint board', backlog: 'Backlog and sprints', reports: 'Reports', projects: 'Projects', people: 'People', settings: 'Settings' },
    noProjects: 'The team works in no project yet',
    noProjectsHint: 'Create a project in the team’s space, or ask a project’s admin to add the team in its settings.',
    sharedBy: 'a project of team “{{team}}”',
    own: 'the team’s project',
    sprints: 'Sprints',
    parallel: 'Several sprints at once',
    parallelHint: 'A team usually runs one sprint. Turn this on if it runs parallel sprints, say for different streams of work.',
  },
  tracker: {
    alsoTeams: 'Also working in the project',
    allTeams: 'All teams',
    alsoTeamsHint: 'Their people become members, and the project’s issues show in their backlogs. Teams can be changed in the project’s settings.',
    quickProject: 'Project of the new issue',
    noTeam: 'No team',
    linkHint: 'Start typing the name of an issue, board or project',
    linkNothing: 'Nothing found',
    noDescription: 'No description',
    alsoEditing: 'Also editing: {{names}}',
    customFields: 'Fields from {{system}} ({{count}})',
    time: {
      title: 'Time',
      log: 'Log time',
      logN: 'Log {{time}}',
      spent: '{{spent}} spent',
      spentOf: '{{spent}} of {{estimate}} spent',
      placeholder: '1h 30m',
      howLong: 'How long',
      day: 'Day',
      note: 'What was done (optional)',
      remove: 'Delete worklog',
      person: 'Who',
      total: 'Total',
      prevWeek: 'Previous week',
      nextWeek: 'Next week',
      thisWeek: 'This week',
      empty: 'No time logged this week.',
      history: { added: 'logged {{time}} on {{day}}', changed: 'changed a worklog: {{time}} on {{day}}', removed: 'deleted a worklog of {{time}} on {{day}}' },
    },
    lanes: {
      title: 'Swimlanes',
      project: 'project',
      none: 'none',
      assignee: 'assignee',
      epic: 'epic',
      priority: 'priority',
      unassigned: 'Unassigned',
      noEpic: 'No epic',
      activeSprint: 'Only sprint “{{name}}”',
      ofLimit: '{{count}} of {{limit}}',
      overLimit: 'Over the limit of {{limit}}',
    },
    myIssues: 'My issues',
    projects: 'Projects',
    otherTeams: 'Other teams',
    newProject: 'New project',
    newProjectHint: 'A project is a set of issues with its own key, statuses and sprints.',
    firstProject: 'Create the first project',
    projectName: 'Name',
    projectNamePlaceholder: 'For example, Payments',
    projectKey: 'Key',
    team: 'Team',
    keyHint: 'The key starts every issue number: {{example}}. Latin letters and digits, up to 10.',
    onBoards: 'On boards',
    issueNotFound: 'The issue does not exist or you have no access to it.',
    toPage: 'Open full page',
    toPanel: 'Back to the project column',
    keyChoices: '{{key}} exists in several of your organizations — which one to open?',
    preset: { assigned: 'Assigned to me', reported: 'Reported by me', open: 'All open' },
    view: { board: 'Board', list: 'List', backlog: 'Backlog and sprints', reports: 'Reports', time: 'Time', settings: 'Settings' },
    reports: {
      none: 'Reports appear once the first sprint starts.',
      committed_one: 'committed: {{count}} issue',
      committed_few: 'committed: {{count}} issues',
      committed_many: 'committed: {{count}} issues',
      committed_other: 'committed: {{count}} issues',
      added_one: 'added on the way: {{count}}',
      added_few: 'added on the way: {{count}}',
      added_many: 'added on the way: {{count}}',
      added_other: 'added on the way: {{count}}',
      removed_one: 'removed: {{count}}',
      removed_few: 'removed: {{count}}',
      removed_many: 'removed: {{count}}',
      removed_other: 'removed: {{count}}',
      done_one: 'done: {{count}}',
      done_few: 'done: {{count}}',
      done_many: 'done: {{count}}',
      done_other: 'done: {{count}}',
      notDone_one: 'not done: {{count}}',
      notDone_few: 'not done: {{count}}',
      notDone_many: 'not done: {{count}}',
      notDone_other: 'not done: {{count}}',
      burndown: 'Burndown: what is left',
      velocity: 'Team velocity',
      noVelocity: 'Velocity appears after the first completed sprint.',
      committedLegend: 'committed',
      doneLegend: 'done',
      average: '{{value}} {{unit}} per sprint on average',
      list: { done: 'Done', notDone: 'Not done', added: 'Added on the way', removed: 'Removed from the sprint' },
    },
    queryPlaceholder: 'status: open assignee: me #urgent',
    queryHelp:
      'Fields: status, assignee, reporter, priority, type, label, sprint, due, created, updated, project.\nValues: me, none, open, resolved, today, -7d.\n#label or #priority for short. -field: value excludes.\nsort by: priority, due, updated. Plain words search titles, descriptions and comments.',
    queryWarnings: 'Not understood: {{list}}',
    newIssue: 'Issue',
    newIssueIn: 'New issue in “{{project}}”',
    titlePlaceholder: 'What needs to be done?',
    createMore: 'Create another',
    created: '{{key}} created',
    unassigned: 'Unassigned',
    nobody: 'nobody',
    someone: 'Someone',
    backlog: 'Backlog',
    sprintActive: 'Active',
    copyLink: 'Copy link',
    linkCopied: 'Link copied',
    delete: 'Delete issue',
    deleted: '{{key}} deleted',
    comments: 'Comments',
    history: 'History',
    createdUpdated: 'Created {{created}} · updated {{updated}}',
    descriptionPlaceholder: 'Description, acceptance criteria, links to boards…',
    estimatePlaceholder: 'e.g. 2h 30m',
    addLabel: 'Add a label',
    noComments: 'No comments yet.',
    commentPlaceholder: 'Comment… @ to mention, Enter to send',
    send: 'Send',
    importedFrom: 'Imported from {{system}}:',
    assigneeThere: 'assignee there: {{name}}',
    system: { jira: 'Jira', youtrack: 'YouTrack', yandex: 'Yandex Tracker', kaiten: 'Kaiten', trello: 'Trello', csv: 'a table' },
    historyImported: 'imported the issue from {{system}}',
    notHere: 'not in the team',
    historyCreated: 'created the issue',
    historyDeleted: 'deleted the issue',
    historyDescription: 'changed the description',
    historyChanged: 'changed {{field}}:',
    field: {
      status: 'Status',
      team: 'Team',
      assignee: 'Assignee',
      priority: 'Priority',
      priorityShort: 'Pr.',
      type: 'Type',
      sprint: 'Sprint',
      due: 'Due',
      estimate: 'Estimate',
      points: 'Story points',
      labels: 'Labels',
      key: 'Key',
      title: 'Title',
      updated: 'Updated',
      parent: 'Parent',
    },
    command: {
      open: 'Command line',
      button: 'Command',
      placeholder: 'status: in progress assignee: me due: tomorrow estimate: 2h',
      placeholderBulk: 'status: done assignee: me label: +urgent',
      hint: 'Fields as in search: status, assignee, priority, type, sprint, label (+add, -remove), due (tomorrow, +3d, 2026-11-01), estimate (2h 30m); “none” clears. Words: take, close, reopen, me, #label. Enter applies, Esc closes.',
      apply: 'Apply (Enter)',
      me: 'me',
      clear: 'clear',
      oneOnly: 'one issue only',
      notInBulk: 'This field does not change for several issues at once',
      applied_one: '{{count}} issue changed',
      applied_few: '{{count}} issues changed',
      applied_many: '{{count}} issues changed',
      applied_other: '{{count}} issues changed',
      error: {
        field: 'no field “{{text}}”',
        status: 'no status “{{text}}”',
        person: 'no member “{{text}}”',
        ambiguous: '“{{text}}” fits several — be more specific',
        priority: 'no priority “{{text}}”',
        type: 'no type “{{text}}”',
        sprint: 'no open sprint “{{text}}”',
        team: 'the project has no team “{{text}}”',
        date: 'cannot read the date “{{text}}”',
        estimate: 'cannot read the estimate “{{text}}”',
        points: 'story points are a number from 0 to 1000, not “{{text}}”',
        word: 'cannot understand “{{text}}”',
      },
    },
    priority: { 0: 'Critical', 1: 'High', 2: 'Normal', 3: 'Low' },
    type: { task: 'Task', bug: 'Bug', story: 'Story', epic: 'Epic', subtask: 'Subtask' },
    units: { d: 'd', h: 'h', m: 'm', b: 'B', kb: 'KB', mb: 'MB', gb: 'GB' },
    addIssue: 'Add issue',
    quickPlaceholder: 'Title and Enter (Shift+Enter for another)',
    noIssues: 'No issues match this query.',
    issuesCount_one: '{{count}} issue',
    issuesCount_few: '{{count}} issues',
    issuesCount_many: '{{count}} issues',
    issuesCount_other: '{{count}} issues',
    doneOf: '{{done}} of {{total}} done',
    startSprint: 'Start sprint',
    closeSprint: 'Complete',
    newSprint: 'Sprint',
    newSprintTitle: 'New sprint',
    sprintEmpty: 'Drag issues here from the backlog.',
    backlogEmpty: 'The backlog is empty.',
    sprintN: 'Sprint {{n}}',
    sprintName: 'Name',
    sprintGoal: 'Sprint goal',
    sprintStarts: 'Starts',
    sprintEnds: 'Ends',
    sp: 'SP',
    storyPoints: 'Story points',
    sprints: {
      edit: 'Edit sprint',
      editTitle: 'Sprint',
      startTitle: 'Start “{{name}}”',
      goalHint: 'What the team wants by the end of the sprint',
      length: 'Length',
      weeks_one: '{{count}} week',
      weeks_few: '{{count}} weeks',
      weeks_many: '{{count}} weeks',
      weeks_other: '{{count}} weeks',
      customDates: 'Custom dates',
      capacityPoints: 'Capacity, SP',
      capacityHours: 'Capacity, hours',
      capacityHint: 'How much the team takes in — optional',
      capacityOf: '{{load}} of {{capacity}} {{unit}} taken',
      hoursShort: 'h',
      moveUp: 'Move up',
      moveDown: 'Move down',
      delete: 'Delete sprint',
      deleteConfirm: 'Delete the sprint “{{name}}”? Its issues go back to the backlog.',
      completeTitle: 'Complete “{{name}}”',
      complete: 'Complete sprint',
      issuesDone: 'issues done',
      pointsDone: 'SP done',
      openGoTo_one: '{{count}} unfinished issue — where does it go?',
      openGoTo_few: '{{count}} unfinished issues — where do they go?',
      openGoTo_many: '{{count}} unfinished issues — where do they go?',
      openGoTo_other: '{{count}} unfinished issues — where do they go?',
      toBacklog: 'To the backlog',
      toNewSprint: 'To a new sprint',
      allDone: 'Every issue of the sprint is done.',
      closed: 'Sprint “{{name}}” completed',
      daysLeft_one: '{{count}} day left',
      daysLeft_few: '{{count}} days left',
      daysLeft_many: '{{count}} days left',
      daysLeft_other: '{{count}} days left',
      lastDay: 'last day',
      overdue_one: 'ended {{count}} day ago',
      overdue_few: 'ended {{count}} days ago',
      overdue_many: 'ended {{count}} days ago',
      overdue_other: 'ended {{count}} days ago',
      planned: 'Planned',
      pointsProgress: '{{done}} of {{total}} SP done',
      pointsHint: 'e.g. 3',
      boardShows: 'Sprint',
      current: 'Current: {{name}}',
      currentAll_one: 'The running sprint',
      currentAll_few: 'The running sprints ({{count}})',
      currentAll_many: 'The running sprints ({{count}})',
      currentAll_other: 'The running sprints ({{count}})',
      allIssues: 'All issues',
      byPeople: 'By person',
      load: 'Load in the sprint',
      openOf: '{{open}} of {{total}} open',
      picked_one: '{{count}} issue picked — drag any of them',
      picked_few: '{{count}} issues picked — drag any of them',
      picked_many: '{{count}} issues picked — drag any of them',
      picked_other: '{{count}} issues picked — drag any of them',
      unpick: 'Clear selection',
      startNew: 'Start a new sprint',
      newOption: '+ New sprint',
      noneRunning: 'No sprint is running.',
      nextIs: 'Next up: “{{name}}”.',
      whatIs: 'A sprint is the work for 1–4 weeks, with a goal and an end date.',
      create: 'Create a sprint',
      planInBacklog: 'Plan in the backlog',
      hideBanner: 'Hide: we work without sprints',
    },
    noPeople: 'Nobody found',
    links: {
      title: 'Links',
      add: 'Link',
      remove: 'Remove the link to {{key}}',
      kind: 'Link type',
      searchPlaceholder: 'Issue key or title',
      already: 'already linked',
      blocked: 'Blocked',
      blockedBy: 'Blocked, waiting for:',
      rel: { blocks: 'Blocks', blocked_by: 'Blocked by', relates: 'Relates to', duplicates: 'Duplicates', duplicated_by: 'Duplicated by' },
      historyAdded: 'added a link: {{relation}} {{key}}',
      historyRemoved: 'removed a link: {{relation}} {{key}}',
    },
    files: {
      title: 'Files',
      attach: 'Attach',
      hint: 'Drop files here or paste a screenshot. Up to 25 MB each.',
      drop: 'Drop to attach',
      tooLarge: '“{{name}}” is larger than 25 MB and cannot be attached.',
      download: 'Download',
      remove: 'Delete file',
      removed: '“{{name}}” deleted',
      open: 'Open “{{name}}”',
      historyAdded: 'attached “{{name}}”',
      historyRemoved: 'deleted the file “{{name}}”',
    },
    filters: {
      empty: 'No saved filters',
      emptyHint: 'Search for issues and press “Save” next to the query: the filter shows up here.',
      subscribe: 'Subscribe',
      subscribed: 'The filter’s issues will come by email {{when}}',
      unsubscribed: 'Filter emails are off',
      digestHint: 'The filter’s issues by email, at 09:00 Moscow time',
      digest: { daily: 'every morning', weekly: 'on Mondays', off: 'don’t send' },
      title: 'Filters',
      save: 'Save',
      saveHint: 'Save the query as a filter: it appears in My issues, under Filters',
      saveTitle: 'Save filter',
      name: 'Name',
      namePlaceholder: 'For example, Release bugs',
      saveButton: 'Save',
      share: 'Show to a team',
      shareWith: 'Show to “{{team}}”',
      shareHint: 'everyone sees only the issues they may see',
      unshare: 'Keep to myself',
      inProject: 'in {{key}}',
      everywhere: 'in every project',
      sharedWith: 'shared with “{{team}}”',
      private: 'only you see it',
      by: 'by {{name}}',
      reset: 'Reset',
      saveChanges: 'Save changes',
      saved: 'Filter saved',
      delete: 'Delete filter',
      deleteTitle: 'Delete the filter “{{name}}”?',
      deleteShared: 'It disappears for the whole team. The issues stay.',
      deleted: '“{{name}}” deleted',
    },
    bulk: {
      toolbar: 'Actions on the picked issues',
      selectAll: 'Pick all',
      select: 'Pick {{key}}',
      selected_one: '{{count}} picked',
      selected_few: '{{count}} picked',
      selected_many: '{{count}} picked',
      selected_other: '{{count}} picked',
      noCommonStatuses: 'The projects of these issues share no status',
      sprintOneProject: 'A sprint can be set for issues of one project',
      addLabel: 'Add a label and press Enter',
      removeLabel: 'Remove a label',
      delete: 'Delete the picked issues',
      deleteTitle_one: 'Delete {{count}} issue?',
      deleteTitle_few: 'Delete {{count}} issues?',
      deleteTitle_many: 'Delete {{count}} issues?',
      deleteTitle_other: 'Delete {{count}} issues?',
      deleteHint: 'They leave lists and boards but stay in the project’s history.',
      deleteConfirm: 'Delete',
      clear: 'Clear the selection',
      done_one: '{{count}} issue changed',
      done_few: '{{count}} issues changed',
      done_many: '{{count}} issues changed',
      done_other: '{{count}} issues changed',
      partly: 'Changed: {{changed}}, failed: {{failed}}. {{reasons}} Those stay picked.',
    },
    dev: {
      title: 'Development',
      pr: { open: 'open', draft: 'draft', merged: 'merged', closed: 'closed' },
      ci: { pending: 'CI: queued', running: 'CI: running', success: 'CI: passed', failed: 'CI: failed', canceled: 'CI: canceled' },
      moreCommits_one: '{{count}} more commit',
      moreCommits_few: '{{count}} more commits',
      moreCommits_many: '{{count}} more commits',
      moreCommits_other: '{{count}} more commits',
      fewer: 'show fewer',
      closedBy: 'closed the issue with',
      notClosed: 'did not close the issue with',
    },
    git: {
      title: 'Git',
      hint: 'An issue key ({{example}}) in a branch name, commit or pull request links it to the issue, in any project of the team. The issue shows its state and CI.',
      provider: { github: 'GitHub', gitlab: 'GitLab', gitea: 'Gitea / Forgejo', bitbucket: 'Bitbucket' },
      connect: 'Connect repository',
      connectTitle: 'Connect a repository',
      connectHint: 'Vatman then shows a webhook address and a secret to paste into the repository settings.',
      url: 'Repository address',
      urlPlaceholder: { github: 'https://github.com/team/project', gitlab: 'https://gitlab.com/group/project', gitea: 'https://git.example.com/team/project', bitbucket: 'https://bitbucket.org/team/project' },
      setup: 'Setup',
      noEvents: 'no webhook received yet',
      lastEvent: 'last event {{when}}: {{event}}',
      linked_one: 'linked to {{count}} issue',
      linked_few: 'linked to {{count}} issues',
      linked_many: 'linked to {{count}} issues',
      linked_other: 'linked to {{count}} issues',
      hookUrl: 'Webhook address (Payload URL)',
      secret: 'Secret',
      token: 'Secret token',
      autoClose: 'Close issues on merge',
      autoCloseHint: '“Fixes {{example}}” in a merged pull request or a commit on the default branch moves the issue to Done, under the project’s rules',
      rotate: 'New secret',
      disconnect: 'Disconnect',
      steps: {
        github: 'In the repository: Settings → Webhooks → Add webhook. Payload URL and Secret are below, Content type application/json. Events: “Let me select individual events” → Pushes, Pull requests, Branch or tag creation and deletion, Statuses, Check suites, Workflow runs.',
        gitlab: 'In the project: Settings → Webhooks → Add new webhook. URL and Secret token are below. Triggers: Push events, Merge request events, Pipeline events.',
        gitea: 'In the repository: Settings → Webhooks → Add webhook → Gitea (or Forgejo). Target URL and Secret are below, POST, application/json. Events: Push, Pull request, Create and Delete; for CI, Status if your version sends it.',
        bitbucket: 'Bitbucket Cloud: Repository settings → Webhooks → Add webhook; URL and Secret are below; triggers: Push, Pull request (Created, Updated, Merged, Declined), Build status (Created, Updated). Data Center: Repository settings → Webhooks → Create webhook; URL and Secret are below; events: Repository → Push, Pull request → Opened, Modified, Merged, Declined. With Data Center the issue shows branches and pull requests, not commits.',
      },
    },
    watch: { start: 'Watch the issue: comments and status changes reach your inbox and email', stop: 'Stop watching', who: 'Watching: {{names}}' },
  },
  trackerSettings: {
    teams: {
      title: 'Teams',
      hint: 'Teams of the organization that work in the project. Their people get a role in it, and issues get a Team field. The home team’s space is not opened to them.',
      home: 'home team: the project’s space and settings',
      role: 'Role of team {{name}}',
      roles: { member: 'Works in the project', viewer: 'Only reads' },
      add: 'Add a team',
      pick: 'Choose a team…',
      none: 'No other teams in the organization',
      remove: 'Take the team off the project: its issues go to the home team',
    },
    estimation: 'Estimate issues in',
    estimationTime: 'Time (hours and minutes)',
    estimationHint: 'Story points are a relative size (1, 2, 3, 5, 8…); sprint capacity, burndown and velocity use them. Time tracking works either way.',
    orgAccess: {
      title: 'Organization access',
      label: 'Everyone in “{{name}}”',
      hint: 'What people of the organization outside the team can do in the project. With edit access they work on issues and can be assignees.',
    },
    members: {
      title: 'People and roles',
      hint: 'By default the project’s team works in it: team owners and admins run it, members work on issues. Here you can give someone another role or bring in a colleague from the organization.',
      fromOrg: 'from the organization',
      guest: 'guest',
      invite: 'Invite',
      invitePlaceholder: 'Email of a colleague or a guest',
      inviteHint: 'Someone from outside becomes a guest: they see this project only. Without an account they get an email with a link.',
      invitedAdded: '{{email}} added to the project',
      invitedSent: 'Invitation sent to {{email}}',
      pending: 'waiting for an answer',
      cancelInvite: 'Cancel the invitation',
      teamAdmin: 'team admin',
      role: 'Role: {{name}}',
      reset: 'Back to the team role',
      remove: 'Remove from the project',
      add: 'Add someone from the organization',
      search: 'Name or email',
      newRole: 'The new person’s role',
      nobody: 'Nobody found',
      roles: { admin: 'Admin', member: 'Member', viewer: 'Viewer', reporter: 'Reporter' },
      about: {
        admin: 'settings, statuses, roles',
        member: 'creates and works on issues, logs time',
        viewer: 'sees issues and the board, changes nothing',
        reporter: 'files issues and comments, the team does the rest',
      },
    },
    wipLimit: 'Column limit (WIP)',
    wipNone: 'no limit',
    general: 'Project',
    description: 'Description',
    descriptionPlaceholder: 'What the project is about, links to boards and documents',
    statuses: 'Statuses',
    statusesHint: 'The board’s columns, in order. The category decides whether an issue is open or done; rules say which statuses lead here and what must be filled in by then.',
    move: 'Move “{{name}}”',
    moveHint: 'Drag, or press ↑ / ↓',
    color: 'Color',
    statusName: 'Status name',
    category: 'Category',
    cat: { todo: 'To do', doing: 'In progress', done: 'Done' },
    rules: 'Rules',
    rulesN_one: '{{count}} rule',
    rulesN_few: '{{count}} rules',
    rulesN_many: '{{count}} rules',
    rulesN_other: '{{count}} rules',
    deleteStatus: 'Delete status “{{name}}”',
    allowedFrom: 'An issue can enter “{{name}}”',
    fromAny: 'from any status',
    fromSome: 'only from the chosen ones',
    fromNone: 'No status is chosen: nothing will be able to enter this one.',
    required: 'Must be filled in by then',
    field: { assignee: 'Assignee', estimate: 'Estimate', due: 'Due date', description: 'Description', labels: 'Labels', sprint: 'Sprint' },
    deleteTitle: 'Delete status “{{name}}”?',
    deleteHint: 'Its issues move to another status, and other statuses’ rules stop naming it.',
    moveTo: 'Move its issues to',
    deleteHistory: 'The move is recorded in the history of each {{key}} issue.',
    deleteConfirm: 'Delete status',
    deleted: 'Status “{{name}}” deleted',
    newStatus: 'New status',
    addStatus: 'Add',
    archive: 'Archive',
    archiveHint: 'The project leaves the list; its issues, links and history stay. You can bring it back at any time.',
    archiveButton: 'Archive project',
    archiveTitle: 'Archive “{{name}}”?',
    archived: '“{{name}}” is archived',
    archivedBanner: 'The project “{{name}}” is archived.',
    restore: 'Restore',
    openSettings: 'Settings',
    archiveNav: 'Archive · {{count}}',
  },
  backlinks: {
    title: 'Linked from',
    hint: 'Boards and issues with a link to this one: a link card, its address in text or [[…]] in an issue.',
    empty: 'Nothing links to this board yet.',
  },
  history: {
    title: 'Version history',
    save: 'Save current version',
    namePlaceholder: 'Version name',
    name: 'Name this version',
    auto: 'Autosave',
    saved: 'Version saved',
    empty: 'No versions yet. They are saved automatically every 10 minutes of work and when everyone leaves the board.',
    today: 'Today',
    yesterday: 'Yesterday',
    restore: 'Restore',
    restoreTitle: 'Restore this version?',
    restoreConfirm: 'The board will look like this version for everyone. The current state is kept in history, and you can undo the restore as usual.',
    restored: 'Version restored. Undo with {{keys}}',
    beforeRestore: 'Before restoring “{{name}}” ({{time}})',
    closePreview: 'Close preview',
    offline: 'No connection to the server. You can restore a version once the board is back online.',
  },
  live: {
    react: 'Reactions',
    reactKeys: 'Keys 1–8 send a reaction from your cursor that everyone sees.',
    raiseHand: 'Raise hand',
    lowerHand: 'Lower hand',
    hands: 'Raised hands',
    handsCount: 'Raised hands: {{n}}',
    noHands: 'Nobody has a hand up right now.',
    lowerAll: 'Lower all hands',
    lowered: 'All hands lowered',
    raised: '{{name}} raised a hand',
    raisedMany: 'Hands raised: {{n}}',
    you: 'you',
    stampHint: 'Click an item to stamp it; click your own stamp to take it back.',
    stampedBy: 'Stamped by',
    andMore: 'and {{n}} more',
    laserHint: 'Move your cursor: everyone sees a red laser.',
  },
  presentation: {
    exit: 'Exit presentation',
    slide: 'Slide {{n}} of {{total}}',
  },
  links: {
    insert: 'Link',
    title: 'Insert link',
    hint: 'Adds a card with a preview. YouTube, RUTUBE and VK Video videos and Figma files play right on the board.',
    placeholder: 'https://…',
    add: 'Insert',
    invalid: "That doesn't look like a link. Start with https://",
    open: 'Open link',
    play: 'Play',
    view: 'Open viewer',
    closePlayer: 'Close player',
  },
  invite: {
    accepting: 'Accepting the invitation…',
    needLogin: 'Sign in or create an account to accept the invitation.',
    failed: 'This invitation is invalid or was already accepted.',
  },
  profile: {
    title: 'Profile',
    color: 'Cursor color',
  },
  connect: {
    title: '“{{name}}” asks for access to Vatman',
    text: 'The agent will act as you ({{name}}) and see only what you can see. Choose what it may do.',
    back: 'After you answer you go back to {{host}}. You can disconnect the app in your profile, under “AI agents and API tokens”.',
    allow: 'Allow',
    deny: 'Deny',
    unknown: 'The app is unknown or the link is outdated. Start connecting again.',
  },
  activity: {
    title: 'Activity log',
    hint: 'Who opened access, added and removed people, changed roles, deleted boards and issues, connected repositories. Kept for a year.',
    empty: 'Nothing has happened yet.',
    more: 'Show more',
    someone: 'Someone',
    on: 'turned on',
    off: 'turned off',
    role: { owner: 'owner', admin: 'admin', member: 'member', viewer: 'viewer', commenter: 'commenter', editor: 'editor', reporter: 'reporter' },
    action: {
      'board.created': 'created the board “{{target}}”',
      'board.access': 'changed access to “{{target}}”: link — {{link}}, team — {{team}}, organization — {{org}}',
      'board.invited': 'invited {{count}} people to “{{target}}”',
      'board.member_role': 'made {{subject}} {{role}} on “{{target}}”',
      'board.member_removed': 'removed {{subject}} from “{{target}}”',
      'board.transferred': 'handed “{{target}}” to {{subject}}',
      'board.trashed': 'moved “{{target}}” to the trash',
      'board.restored': 'restored “{{target}}”',
      'board.deleted': 'deleted “{{target}}” for good',
      'team.created': 'created the team “{{target}}”',
      'team.invited': 'invited {{count}} people to the team “{{target}}”',
      'team.member_role': 'made {{subject}} {{role}} in “{{target}}”',
      'team.member_removed': 'removed {{subject}} from “{{target}}”',
      'team.left': 'left the team “{{target}}”',
      'team.agent_created': 'created the agent {{subject}} in the team “{{target}}”',
      'team.agent_removed': 'removed the agent {{subject}} from the team “{{target}}”',
      'team.agent_token_created': 'gave the agent {{subject}} the token “{{title}}”',
      'team.agent_token_revoked': 'revoked a token of the agent {{subject}}',
      'org.settings': 'changed the organization’s settings',
      'org.invited': 'invited {{count}} people to the organization',
      'org.member_role': 'made {{subject}} {{role}} of the organization',
      'org.member_removed': 'removed {{subject}} from the organization',
      'org.left': 'left the organization',
      'org.guest_removed': 'removed the guest {{subject}} from every project and board of the organization',
      'org.agents': '{{state}} AI agents in the organization',
      'project.created': 'created the project {{target}}',
      'space.created': 'created the space “{{target}}”',
      'space.renamed': 'renamed the space “{{title}}” to “{{target}}”',
      'space.deleted': 'deleted the space “{{title}}” in the team “{{target}}”',
      'space.access': 'changed the team’s access to the space “{{target}}”: {{team}}',
      'space.member_role': 'gave {{subject}} the role “{{role}}” in the space “{{target}}”',
      'space.member_removed': 'removed {{subject}} from the space “{{target}}”',
      'board.moved': 'moved the board “{{target}}”',
      'project.moved': 'moved the project {{target}} to another space',
      'project.invited': 'invited someone by email to the project {{target}} as {{role}}',
      'project.archived': 'archived the project {{target}}',
      'project.restored': 'brought back the project {{target}}',
      'project.role': 'made {{subject}} {{role}} in {{target}}',
      'project.role_removed': 'gave {{subject}} back the team role in {{target}}',
      'issue.deleted': 'deleted the issue {{key}} “{{title}}”',
      'repo.connected': 'connected the repository {{target}}',
      'repo.removed': 'disconnected the repository {{target}}',
      'repo.secret_rotated': 'changed the webhook secret of {{target}}',
      'import.started': 'started importing {{key}} from {{system}} into “{{target}}”',
    },
  },
  update: { text: 'A new version of Vatman is out — reload the page to keep working.', reload: 'Reload' },
  tokens: {
    connector: 'No token needed: in claude.ai and Claude Desktop add a custom connector with this address — you sign in to Vatman and choose what the agent may do.',
    connection: 'connection',
    moduleBoards: 'Boards',
    moduleTracker: 'Issues',
    level: { write: 'Read and write', read: 'Read only', none: 'No access' },
    menu: 'AI agents & API tokens',
    title: 'AI agents & API tokens',
    description: 'A token connects Claude and other AI agents to your boards and tasks over MCP. The agent acts as you and sees only what you can open; it edits only where you are an editor.',
    name: 'Name',
    access: 'Access',
    write: 'Read & write',
    read: 'Read only',
    expires: 'Expires',
    days_one: '{{count}} day',
    days_few: '{{count}} days',
    days_many: '{{count}} days',
    days_other: '{{count}} days',
    never: 'Never',
    create: 'Create token',
    copyOnce: 'Copy the token now: it will not be shown again. Keep it secret, like a password.',
    token: 'Token',
    claudeCode: 'Connect in Claude Code',
    otherClients: 'Claude Desktop and other MCP clients: URL {{url}}, header Authorization: Bearer <token>.',
    empty: 'No tokens yet.',
    created: 'created {{when}}',
    used: 'used {{when}}',
    unused: 'never used',
    expiresAt: 'expires {{when}}',
    expired: 'expired',
    revoke: 'Revoke',
    revokeTitle: 'Revoke this token?',
    revokeText: 'Agents using “{{name}}” will lose access to your boards and tasks within a minute.',
    tabTokens: 'Tokens',
    tabLog: 'Activity log',
    tabAgents: 'Agent accounts',
    boards: 'Boards',
    allBoards: 'All my boards and tasks',
    someBoards: 'Selected boards only',
    findBoard: 'Find a board',
    picked_one: '{{count}} board selected',
    picked_few: '{{count}} boards selected',
    picked_many: '{{count}} boards selected',
    picked_other: '{{count}} boards selected',
    boardsCount_one: '{{count}} board',
    boardsCount_few: '{{count}} boards',
    boardsCount_many: '{{count}} boards',
    boardsCount_other: '{{count}} boards',
  },
  issueList: {
    insert: 'Issues from the tracker',
    newTitle: 'Issue list on the board',
    editTitle: 'Issue list',
    hint: 'A live list of issues by a query: it updates by itself, and everyone sees what the tracker lets them see.',
    project: 'Project',
    allProjects: 'All projects',
    query: 'Query',
    queryHint: 'The tracker’s search language: status: open, assignee: me, sprint: current, #label, sort by: priority.',
    name: 'Title',
    add: 'Add to the board',
    untitled: 'Issues',
    loading: 'Loading…',
    empty: 'No issues',
    noAccess: 'No access to these issues',
    failed: 'Could not load the issues',
    more_one: '{{count}} more',
    more_few: '{{count}} more',
    more_many: '{{count}} more',
    more_other: '{{count}} more',
    moreThan_one: 'over {{count}}',
    moreThan_few: 'over {{count}}',
    moreThan_many: 'over {{count}}',
    moreThan_other: 'over {{count}}',
  },
  guests: {
    title: 'Guests',
    allow: 'Allow inviting people from outside',
    hint: 'A guest sees only the boards and projects they were invited to. When off, only the organization’s members can be invited; guests already in stay until removed.',
    empty: 'No guests.',
    remove: 'Remove access',
    removed: '{{name}} no longer sees anything in the organization',
    turnedOn: 'Guests can be invited',
    turnedOff: 'Only members can be invited',
  },
  spaces: {
    sharedFrom: 'A project of team “{{team}}”: your team works in it',
    title: 'Spaces',
    add: 'Create',
    newSpace: 'Space',
    newTitle: 'New space',
    newHint: 'A space gathers the boards and task projects of one topic: a product, a department, a client. Whoever creates it becomes its admin.',
    editTitle: 'Space',
    icon: 'Icon',
    iconUpload: 'Upload your own picture',
    iconNone: 'No icon',
    iconUnreadable: 'Could not open the picture',
    name: 'Name',
    namePlaceholder: 'E.g. Marketing',
    description: 'Description',
    descriptionPlaceholder: 'What is here and for whom',
    team: 'Team',
    teamHint: 'The team gets its own “General” space right away.',
    space: 'Space',
    otherProjects: 'Other projects',
    guestProjects: 'Projects I was invited to',
    collapse: 'Collapse',
    expand: 'Expand',
    moveTo: 'Move to space',
    notFound: 'The space was not found or is not available.',
    default: 'default',
    edit: 'Edit',
    delete: 'Delete space',
    deleteTitle: 'Delete the space?',
    deleteText: 'Boards and projects of “{{name}}” move to the team’s default space; nothing is lost.',
    deleted: 'Space “{{name}}” deleted; its content is in the default space',
    boards: 'Boards',
    tasks: 'Tasks',
    noProjects: 'No task projects here yet',
    noProjectsHint: 'A project with a key (e.g. PAY) holds issues, sprints and a board.',
    breadcrumb: 'Space',
    private: 'private',
    privateOption: 'Private space',
    privateOptionHint: 'Only the people you add and the team admins see it. You can change access later.',
    roles: {
      admin: 'Admin',
      editor: 'Editor',
      commenter: 'Commenter',
      viewer: 'Viewer',
    },
    roleAbout: {
      admin: 'Changes the space and decides who is in it; runs its boards and projects, except private boards.',
      editor: 'Creates boards and projects and works in them.',
      commenter: 'Views and comments.',
      viewer: 'Only views.',
    },
    access: {
      button: 'Access',
      title: 'Access to “{{name}}”',
      hint: 'Who sees the space and what they do with its boards and projects. A board or project may give less — or bring in someone else.',
      team: 'Everyone in “{{name}}”',
      teamLabel: 'Team access',
      teamSize_one: '{{count}} person',
      teamSize_few: '{{count}} people',
      teamSize_many: '{{count}} people',
      teamSize_other: '{{count}} people',
      private: 'No access — private',
      privateHint: 'Private: only the people below',
      defaultOpen: 'The default space is always open to the team: whatever is created without a space lands there.',
      people: 'People',
      teamAdmin: 'Team admin',
      fromOrg: 'from the organization',
      you: 'you',
      roleOf: 'Role: {{name}}',
      remove: 'Remove from the space',
      leave: 'Leave the space',
      left: 'You left “{{name}}”',
      nobody: 'Nobody has a role of their own yet — everyone in the team works by the team’s access.',
      nobodyPrivate: 'Add people: apart from the team admins, nobody gets in here.',
      add: 'Add someone',
      search: 'A colleague’s name or email',
      newRole: 'Role for the new member',
      noOne: 'No colleagues found.',
    },
  },
  agentAccounts: {
    intro: 'An agent is a team’s service account. People assign it issues and mention it like a colleague; an AI agent (Claude, say) works as it with a token and learns about new work from its notifications. It sees what the team sees.',
    name: 'Agent name',
    namePlaceholder: 'E.g. Claude the developer',
    team: 'Team',
    create: 'Create agent',
    noTeams: 'Team owners and admins create agents.',
    empty: 'No agents yet.',
    badge: 'agent',
    createdBy: 'created by {{name}}',
    newToken: 'Give a token',
    noTokens: 'No tokens: the agent cannot work yet.',
    rename: 'Rename',
    remove: 'Remove agent',
    removeTitle: 'Remove the agent?',
    removeText: '“{{name}}” will stop working: its tokens are revoked and its open issues lose their assignee. Its comments and edits stay.',
    removed: 'Agent removed',
    created: 'Agent created — give it a token',
  },
  agents: {
    orgTitle: 'AI agents',
    allow: 'Allow AI agents to work with the organization’s boards',
    allowHint: 'Claude and other agents connect over MCP with members’ tokens. When this is off, agents can neither see nor change the boards of the organization’s teams, even with a valid token.',
    statusOn: 'AI agents may work with the organization’s boards. An admin can change this.',
    statusOff: 'An admin turned AI agents off for the organization’s boards.',
    turnedOn: 'AI agents turned on',
    turnedOff: 'AI agents turned off',
    orgLog: 'Agent activity log',
    everyone: 'Everyone',
    allBoards: 'All boards',
    boardTitle: 'AI agent activity',
    boardHint: 'What agents did on this board: who, with which token, which tool and which items.',
    empty: 'No agent activity yet.',
    more: 'Show more',
    token: 'token “{{name}}”',
    ms: 'ms',
    items_one: '{{count}} item',
    items_few: '{{count}} items',
    items_many: '{{count}} items',
    items_other: '{{count}} items',
    tools: {
      list_boards: 'Listed boards',
      create_board: 'Created a board',
      get_board: 'Read the board',
      search_board_text: 'Searched the board',
      add_stickies: 'Added stickies',
      add_shapes: 'Added shapes',
      add_text: 'Added text',
      add_frame: 'Added a frame',
      connect: 'Connected items',
      update_items: 'Changed items',
      move_items: 'Moved items',
      delete_items: 'Deleted items',
      create_mind_map: 'Drew a mind map',
      create_flowchart: 'Drew a flowchart',
      insert_template: 'Inserted a template',
      get_board_image: 'Looked at the board image',
    },
  },
};

export default en;
